import { FC, Fragment } from 'react'
import cn from 'classnames'
import type { Field } from '@/api/types/crm/crm.types'
import LeadField from '../../components/LeadField/LeadField'
import classes from './index.module.css'
import compactFieldClasses from '@pages/Admin/CRM/styles/compactLeadFields.module.css'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import {
  buildLeadPhoneFieldSettings,
  getLeadFieldValue,
} from '@pages/Admin/CRM/utils/leadFields'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import LeadProfileModalBlock from '../../components/LeadProfileModalBlock/LeadProfileModalBlock'
import {
  filterOptionalSmallFields,
  filterOptionalTextareaFields,
} from './aboutTabFields'

interface NarrowAboutContentProps {
  lead?: LeadProfileLead
  fields: Field[]
  fieldsData: Record<string, string>
  updateFieldData: (uniqueNameField: string, value: string) => void
  /** На узкой ширине модалки текстовые поля дублируем сюда — блок под колонками легко уезжает за скролл. */
  inlineTextareasWhenNarrow?: boolean
  onOpenVkChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenMaxChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenTelegramChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenWazzupChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
}

const NarrowAboutContent: FC<NarrowAboutContentProps> = ({
  lead,
  fields,
  fieldsData,
  updateFieldData,
  inlineTextareasWhenNarrow = false,
  onOpenVkChatWithLead,
  onOpenMaxChatWithLead,
  onOpenTelegramChatWithLead,
  onOpenWazzupChatWithLead,
}) => {
  const access = useFullLeadModalAccess()
  const smallFields = filterOptionalSmallFields(fields)
  const textareaFields = filterOptionalTextareaFields(fields)

  const showTextareasHere =
    inlineTextareasWhenNarrow && textareaFields.length > 0
  const hasSmall = smallFields.length > 0
  const showEmpty =
    smallFields.length === 0 && textareaFields.length === 0
  const onlyTextareasInWideBlock =
    !hasSmall &&
    textareaFields.length > 0 &&
    !showTextareasHere

  const cardTitle =
    !hasSmall && showTextareasHere ? 'Текстовые поля' : 'Дополнительные поля'

  const renderField = (field: Field) => {
    const defaultValue = getLeadFieldValue(lead, field.unique_name) ?? ''
    const currentValue = fieldsData[field.unique_name] ?? ''

    return (
      <LeadField
        key={field.id}
        field={field}
        size="small"
        value={fieldsData[field.unique_name] ?? ''}
        onChange={(val) => updateFieldData(field.unique_name, val)}
        fieldSettings={
          field.type === 'phone_number' &&
          lead &&
          (onOpenVkChatWithLead ||
            onOpenMaxChatWithLead ||
            onOpenTelegramChatWithLead ||
            onOpenWazzupChatWithLead)
            ? buildLeadPhoneFieldSettings({
                canInteractWithIntegrations:
                  access.canInteractWithIntegrations,
                lead,
                field,
                defaultValue,
                currentValue,
                onOpenVkChat: (options) => {
                  onOpenVkChatWithLead?.(lead.id, options)
                },
                onOpenMaxChat: onOpenMaxChatWithLead
                  ? (options) => onOpenMaxChatWithLead(lead.id, options)
                  : undefined,
                onOpenTelegramChat: onOpenTelegramChatWithLead
                  ? (options) => onOpenTelegramChatWithLead(lead.id, options)
                  : undefined,
                onOpenWazzupChat: onOpenWazzupChatWithLead
                  ? (options) => onOpenWazzupChatWithLead(lead.id, options)
                  : undefined,
              })
            : undefined
        }
        disabled={!access.canEditFields}
      />
    )
  }

  return (
    <LeadProfileModalBlock
      aria-label={cardTitle}
      title={cardTitle}
    >
      {showEmpty ? (
        <p className={classes.aboutEmpty}>
          Нет настраиваемых полей. Их можно добавить в настройках воронки.
        </p>
      ) : (
        <>
          {hasSmall ? (
            <div className={cn(classes.listSmallFields, compactFieldClasses.root)}>
              {smallFields.map((field) => (
                <Fragment key={field.id}>
                  {renderField(field)}
                </Fragment>
              ))}
            </div>
          ) : null}

          {showTextareasHere ? (
            <>
              {hasSmall ? (
                <>
                  <div
                    className={classes.aboutSubsectionDivider}
                    role="separator"
                  />
                  <h4 className={classes.aboutSubsectionTitle}>
                    Текстовые поля
                  </h4>
                </>
              ) : null}
              <div className={cn(classes.textareaStack, compactFieldClasses.root)}>
                {textareaFields.map((field) => (
                  <div key={field.id} className={classes.textareaStackItem}>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {onlyTextareasInWideBlock ? (
            <p className={classes.aboutHintBelow}>
              Текстовые поля — в блоке ниже под колонками карточки.
            </p>
          ) : null}
        </>
      )}
    </LeadProfileModalBlock>
  )
}

export default NarrowAboutContent
